import { useMemo } from 'react';

import {
  get,
  isEmpty,
  isNil,
  lowerCase,
  reverse,
  sortBy,
  toLower,
  values,
} from 'lodash';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { PaymentTypes } from '@appTypes/enums';
import { CompletePersonProfile, PaymentLogEntry } from '@appTypes/esb';
import { unitProfilesSel } from '@context/duck/selectors';
import {
  useGetCalendarEventQuery,
  useGetCalendarEventsQuery,
} from '@modules/events/api';
import { currentUserIdSel } from '@modules/paymentLogs';
import {
  useGetPaymentLogCategoriesQuery,
  useGetUnitPaymentLogsQuery,
  useGetUserPaymentLogsQuery,
} from '@modules/paymentLogs/api';
import { LOG_SORT_OPTIONS, MINIMUM_DATE } from '@modules/paymentLogs/constants';
import { pastMemberSel, unitFiltersSel } from '@modules/paymentLogs/duck';
import { transformOrgProfileToUnits } from '@modules/paymentLogs/utils';
import { useGetCompletePersonProfileQuery } from '@modules/rtk/esb-api';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';

export function usePersonPaymentLog() {
  const currentUserId = useSelector(currentUserIdSel);
  const organizationGuid = useSelector(organizationGuidSel);
  const unitProfiles = useSelector(unitProfilesSel);
  const unitId = useMemo<number>(
    () => get(unitProfiles, [organizationGuid, 'sbOrganizationId'], null),
    [organizationGuid, unitProfiles],
  );

  const { isInactive: isPastMember } = useSelector(pastMemberSel);

  const { data, isLoading } = useGetUserPaymentLogsQuery(
    {
      userId: currentUserId,
      params: { unitId },
    },
    {
      skip: isPastMember || !unitId,
    },
  );

  if (isNil(data))
    return {
      loading: isLoading,
      unitId,
      balanceDetails: undefined,
      paymentLogs: [] as PaymentLogEntry[],
    };

  return {
    loading: isLoading,
    unitId,
    balanceDetails: data.balanceDetails,
    paymentLogs: data.paymentLogs,
  };
}

export const usePastMemberPaymentLogs = () => {
  const pastMember = useSelector(pastMemberSel);

  const balanceDetails = pastMember.logs.reduce(
    (curr, log) => {
      const sign = Math.sign(log.amount);

      return {
        balance: curr.balance + log.amount,
        totalCharges:
          sign < 0 ? curr.totalCharges + log.amount : curr.totalCharges,
        totalCredits:
          sign >= 0 ? curr.totalCredits + log.amount : curr.totalCredits,
      };
    },
    {
      balance: 0,
      totalCharges: 0,
      totalCredits: 0,
    },
  );

  return {
    isPastMember: pastMember.isInactive,
    balanceDetails,
    paymentLogs: pastMember.logs,
    pastMember,
  };
};

export function usePersonPaymentLogUnits() {
  const unitProfiles = useSelector(unitProfilesSel);
  const memberUnits = transformOrgProfileToUnits(values(unitProfiles));
  return { loading: false, units: memberUnits || [] };
}

export function usePersonPaymentLogsUnitEvents(unitId: number) {
  const { data, isLoading } = useGetCalendarEventsQuery(
    {
      payload: {
        unitId,
        fromDate: DateTime.now().startOf('year').toFormat('yyyy-LL-dd'),
        toDate: DateTime.now().endOf('year').toFormat('yyyy-LL-dd'),
        excludeSubUnitEvents: true,
      },
    },
    { skip: isNil(unitId) },
  );

  if (isNil(data)) return { loading: isLoading, unitEvents: [] };

  return { loading: isLoading, unitEvents: data };
}

export function usePersonPaymentLogEvent(calendarEventId: number | null) {
  const { data, isLoading } = useGetCalendarEventQuery(
    {
      eventId: calendarEventId ?? 0,
    },
    { skip: isNil(calendarEventId) },
  );

  if (isNil(data)) return { loading: isLoading, event: null };

  return { loading: isLoading, event: data };
}

/**
 * Fetch unit transactions
 */
export function useUnitPaymentLogsHistory() {
  const orgGuid = useSelector(organizationGuidSel);
  const filters = useSelector(unitFiltersSel);

  const queryBody = {
    paymentType:
      filters.paymentType === 'All' ? PaymentTypes.CHARGE : filters.paymentType,
    fromDate: filters.dates.fromDate ?? MINIMUM_DATE,
    toDate: filters.dates.toDate,
    ...(!isNil(filters.dates.paymentDueDate) && {
      paymentDueDate: filters.dates.paymentDueDate,
    }),
  };

  const { isFetching: isUnitLogsLoading, data } = useGetUnitPaymentLogsQuery({
    orgGuid,
    params: queryBody,
  });

  const { isLoading: aditionalUnitLogsLoading, data: additionalData } =
    useGetUnitPaymentLogsQuery(
      {
        orgGuid,
        params: { ...queryBody, paymentType: PaymentTypes.PAYMENT },
      },
      {
        skip: filters.paymentType !== 'All',
      },
    );

  const lowercaseSearch = lowerCase(filters.searchFilter);

  const apiData =
    filters.paymentType === 'All'
      ? [...(data ?? []), ...(additionalData ?? [])]
      : data ?? [];

  const unsortedUnitLogs =
    apiData.filter(paymentLog => {
      if (paymentLog.userId || paymentLog.deleted) {
        return false;
      }

      const isInSearch =
        lowerCase(paymentLog.description).includes(lowercaseSearch) ||
        lowerCase(paymentLog.category).includes(lowercaseSearch);

      return isInSearch;
    }) ?? [];

  const sortedUnitLogs = sortBy(unsortedUnitLogs, log =>
    toLower(log[filters.sorter.sortBy]),
  );

  const unitLogs =
    LOG_SORT_OPTIONS.includes(filters.sorter.sortBy) &&
    filters.sorter.order === 'descend'
      ? reverse(sortedUnitLogs)
      : sortedUnitLogs;

  return {
    isUnitLogsLoading: isUnitLogsLoading || aditionalUnitLogsLoading,
    unitLogs,
  };
}

export function usePaymentLogsCategories(
  unitId: number,
  organizationGuid: string,
) {
  const { data, isLoading } = useGetPaymentLogCategoriesQuery(
    {
      unitId,
      organizationGuid,
    },
    {
      skip: isNil(unitId) || `${unitId}` === '',
      refetchOnMountOrArgChange: 600,
    },
  );

  if (isNil(data)) return { loading: isLoading, categories: [] };

  return { loading: isLoading, categories: data };
}

type UsePaymentLogPersonProfile = {
  profile: CompletePersonProfile | Record<string, never>;
  loading: boolean;
};

export const usePaymentLogPersonProfile = (
  personGuid: string,
): UsePaymentLogPersonProfile => {
  const { isInactive: isPastMember } = useSelector(pastMemberSel);
  const { data, isLoading: isProfileLoading } =
    useGetCompletePersonProfileQuery(
      {
        personGuid: personGuid,
      },
      {
        skip: isEmpty(personGuid) || isPastMember,
      },
    );

  if (isNil(data)) return { loading: isProfileLoading, profile: {} };

  return { loading: isProfileLoading, profile: data };
};
